import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/Settings.js EmailTab
//     Real fields: Subject line, Email body (with token reference panel),
//     and a "Notify me when a client completes a request" toggle.
//     Tokens available: {{client_name}}, {{bookkeeper_name}},
//     {{request_title}}, {{link}}.
//     Reminder template (separate tab) has same set plus {{days_since_request}}.
//   - src/pages/Settings.js ProfileTab
//     Practice name and full name fields. These feed the {{bookkeeper_name}}
//     token through the display_name field on bookkeeper_settings.
//   - supabase/functions/send-email/index.ts
//     From address is hardcoded "Receipt Chaser <noreply@receiptchaser.com>".
//     No practice name in the From line. Practice name renders inside the
//     subject and body via tokens only.
//     Email template: teal header bar with "RECEIPT CHASER" text (no logo),
//     white card with body text and a button, no per-bookkeeper styling.
//     reply_to is NOT set on request emails -- replies go to the noreply
//     address.
// Removed:
//   - "Logo upload" -- no such feature exists in the app
//   - "Accent colour" -- no colour picker exists; all emails use Receipt
//     Chaser's standard teal
//   - "Sign-off line" as a separate field -- doesn't exist; the sign-off
//     is whatever the bookkeeper types at the end of the body field
//   - "Practice -> Branding" tab -- doesn't exist; email customisation is
//     on the "Email template" tab
//   - "From line shows 'Acme Bookkeeping (via Receipt Chaser)'" -- false.
//     From is always "Receipt Chaser <noreply@receiptchaser.com>". The
//     practice name appears in the subject/body via tokens.
//   - "Replies go to your practice contact email" -- false. Reply-To
//     isn't set; replies go to noreply.
// Pass 2: this article is now essentially rewritten from scratch. The
// previous version described features that don't exist at all. The real
// article is much smaller because there's far less to customise than
// the old article implied.

export default {
  title:   'Customising your client-facing emails',
  summary: "What you can change about the request and reminder emails, and what's fixed.",
  body: (
    <>
      <P>
        The receipt request email is the first thing your client sees,
        so it's worth getting it right. Receipt Chaser keeps the
        customisation simple: the subject line and the body text are
        yours to write, and your practice name slots in automatically.
        The rest (the layout, the colours, the sender address) is
        fixed so we can keep deliverability high.
      </P>

      <H>What you can change</H>
      <UL>
        <Li>
          <strong>Subject line.</strong> The text in your client's
          inbox preview. The default is "Action required: receipts
          needed for {'{{request_title}}'}".
        </Li>
        <Li>
          <strong>Email body.</strong> The full message your client
          reads. Default text covers the basics, but rewriting it in
          your own voice usually improves response rates.
        </Li>
        <Li>
          <strong>Display name.</strong> What appears wherever{' '}
          <Code>{'{{bookkeeper_name}}'}</Code> is used. Set indirectly
          through your <strong>practice name</strong> and{' '}
          <strong>full name</strong> on{' '}
          <Code>Settings → Profile</Code>.
        </Li>
        <Li>
          <strong>Notify-on-completion.</strong> Whether you receive
          an email when a client finishes a request. Default on. The
          toggle is on the same Email template tab.
        </Li>
      </UL>

      <H>Where to set them</H>
      <P>
        Subject, body, and the completion-notification toggle live on{' '}
        <Code>Settings → Email template</Code>. Reminder templates
        live on a separate tab,{' '}
        <Code>Settings → Auto-reminders</Code>, with the same
        tokens plus one extra (<Code>{'{{days_since_request}}'}</Code>,
        which renders as "X days ago").
      </P>

      <Screenshot
        src="/support-screenshots/11 - email-template.jpg"
        alt="The Settings page with the Email template tab selected. From top: an available-tokens reference panel listing the four tokens, a subject line input, an email body textarea pre-filled with default content using the tokens, a 'Notify me when a client completes a request' toggle, and Save / Preview / Reset buttons at the bottom."
        caption="The Email template tab in Settings, showing the token reference, the subject and body editors, the completion-notification toggle, and the Save / Preview / Reset buttons at the bottom."
      />

      <H>The tokens you can use</H>
      <P>
        Anywhere in the subject or body, you can drop in one of these
        tokens. They get replaced with the right value each time an
        email is sent.
      </P>
      <UL>
        <Li>
          <Code>{'{{client_name}}'}</Code>: the client's name (the
          one you typed in the "Client / company name" field when you
          added them, not the contact name)
        </Li>
        <Li>
          <Code>{'{{bookkeeper_name}}'}</Code>: your display name.
          This is your <em>full name at practice name</em> (e.g.
          "Graham at Receipts Bookkeeping Ltd") if both are set,
          otherwise whichever one is set, otherwise the fallback
          "Your bookkeeper".
        </Li>
        <Li>
          <Code>{'{{request_title}}'}</Code>: the title of the
          specific request
        </Li>
        <Li>
          <Code>{'{{link}}'}</Code>: the upload link. In the
          rendered email this becomes a button rather than a raw URL.
        </Li>
        <Li>
          On the reminder template only:{' '}
          <Code>{'{{days_since_request}}'}</Code>: text like "5 days
          ago"
        </Li>
      </UL>

      <H>Previewing what your client will see</H>
      <P>
        On the Email template tab, click the <Code>👁 Preview</Code>{' '}
        button. The current subject and body render with the tokens
        filled in using your display name and a placeholder client.
        Useful for catching weird whitespace or accidental token
        typos before saving.
      </P>
      <P>
        For a full end-to-end test (including the wrap, the button,
        and the colours as the client sees them), send a request to
        yourself. Add yourself as a test client with a personal email
        address, send a request, and the email lands in your own
        inbox as the real thing.
      </P>

      <H>What you can't change</H>
      <UL>
        <Li>
          <strong>The sender address.</strong> Emails come from{' '}
          <Code>Receipt Chaser &lt;noreply@receiptchaser.com&gt;</Code>.
          Your practice name appears in the subject and the body via
          tokens, but not in the From line.
        </Li>
        <Li>
          <strong>The header bar.</strong> Every email has Receipt
          Chaser's teal header at the top. No logo upload, no colour
          picker.
        </Li>
        <Li>
          <strong>The button colour.</strong> The upload button uses
          Receipt Chaser's brand teal. No way to change it.
        </Li>
        <Li>
          <strong>The reply-to address.</strong> Replies to the
          email go to <Code>noreply@receiptchaser.com</Code> and
          aren't read by anyone. Worth telling your client this up
          front if they're likely to ask questions by reply.
        </Li>
      </UL>

      <Callout variant="warning" title="Setting expectations with your client">
        Because replies aren't monitored, the practical advice is to
        include your own email or phone number in the body of the
        message, so clients have an obvious way to ask questions if
        they need to. The default body doesn't do this, so consider
        adding a line like "Any questions, reply to
        graham@receiptsbookkeeping.co.uk or call 0123 456 7890."
      </Callout>

      <H>Writing tips that improve response rates</H>
      <UL>
        <Li>
          Keep it short. A client glancing at their phone is more
          likely to act on three sentences than three paragraphs.
        </Li>
        <Li>
          Be specific about what you need. "Receipts for last
          month's expenses" is better than "Some documents".
        </Li>
        <Li>
          State a deadline. "Please complete by Friday" gets faster
          replies than "When you have a moment".
        </Li>
        <Li>
          Sign off the email body with your real name, not just your
          firm. "Thanks, Graham" reads as a human; "Thanks, Receipts
          Bookkeeping Ltd" reads as an automated system.
        </Li>
      </UL>

      <H>Resetting to the default</H>
      <P>
        Made changes you regret? Click <Code>Reset to default</Code>{' '}
        next to the Save button at the bottom of the tab. The
        default subject and body are restored. (You'll need to click
        Save afterwards to actually persist them.)
      </P>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/setting-up-your-account">Setting up your account</Link>
        </Li>
        <Li>
          <Link to="/support/sending-your-first-request">Sending your first receipt request</Link>
        </Li>
        <Li>
          <Link to="/support/client-didnt-get-email">My client says they didn't get the email</Link>
        </Li>
      </UL>
    </>
  ),
}
